import range from 'lodash-es/range'
import { PDFDocument } from 'pdf-lib'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  NOTIFICATION_TYPE,
} from '../../../constants/common'
import { DocFlowTableCellName } from '../../../constants/docFlow'
import { DocFlowWidgetStatus } from '../DocFlow.constants'
import {
  isDocFlowActiveWidgetMappping,
  isDocFlowActiveWidgetSplitting,
  isDocFlowWidgetStatusCompleted,
  isDocFlowWidgetStatusSkipped,
} from '../DocFlow.utils.widgets'
import { MappingWidgetType } from '../DocFlowMappingWidgets'
import { isDocFlowSplittingTypeArea } from '../DocFlowWidgets'

import { DocFlowMappingProcessCell } from './DocFlowMapping.constants'

export function isNextPhaseDisabled(files) {
  return files?.some(item => item.status !== DocFlowWidgetStatus.Completed)
}

export function getDocFlowMappingActivePreviewFile(files, id) {
  return files?.find(file => String(file.id) === String(id))
}

export function getPreparedActiveFilePreview({
  files,
  mappingPhaseFileActive,
}) {
  const {
    file_id: fileId,
    id,
    ...activeFile
  } = getDocFlowMappingActivePreviewFile(files, mappingPhaseFileActive) ||
  EMPTY_OBJECT

  return {
    ...activeFile,
    id: fileId,
    itemId: id,
  }
}

export function isWidgetInProgress(value) {
  return value === DocFlowWidgetStatus.InProgress
}

export function getInitialActiveWidget(processData) {
  if (processData) {
    switch (true) {
      case isWidgetInProgress(
        processData[DocFlowMappingProcessCell.MappingPhase]
      ):
        return MappingWidgetType.Mapping

      case isWidgetInProgress(
        processData[DocFlowMappingProcessCell.SplittingPhase]
      ):
        return MappingWidgetType.Splitting

      case isWidgetInProgress(
        processData[DocFlowMappingProcessCell.ResultPhase]
      ):
        return MappingWidgetType.Result

      default:
        return MappingWidgetType.Type
    }
  }

  return MappingWidgetType.Type
}

export function isFileInProgress(item) {
  return item[DocFlowTableCellName.Status] === DocFlowWidgetStatus.InProgress
}

export function isDocFlowMappingSplittingAreaVisible({
  activeWidget,
  processData,
}) {
  return (
    isDocFlowSplittingTypeArea(
      processData?.[DocFlowMappingProcessCell.SplittingType]
    ) &&
    isDocFlowActiveWidgetSplitting(activeWidget) &&
    !isDocFlowWidgetStatusSkipped(
      processData?.[DocFlowMappingProcessCell.SplittingPhase]
    )
  )
}

export function isDocFlowMappingAreaVisible({ activeWidget, processData }) {
  return (
    isDocFlowActiveWidgetMappping(activeWidget) &&
    !!processData?.[DocFlowMappingProcessCell.MappingType] &&
    !isDocFlowWidgetStatusSkipped(
      processData?.[DocFlowMappingProcessCell.MappingPhase]
    )
  )
}

export function getDocFlowWidgetActiveAreaValue({
  isMappingAreaVisible,
  isSplittingAreaVisible,
  processData,
}) {
  switch (true) {
    case isMappingAreaVisible:
      return getDocFlowMappingWidgetArea(processData)

    case isSplittingAreaVisible:
      return getDocFlowMappingWidgetSplittingArea(processData)

    default:
      return null
  }
}

export function isDocFlowWidgetMappingDisabled({ activeWidget, processData }) {
  return (
    isDocFlowActiveWidgetMappping(activeWidget) &&
    !isWidgetInProgress(processData?.[DocFlowMappingProcessCell.MappingPhase])
  )
}

export function isDocFlowWidgetSplittingDisabled({
  activeWidget,
  processData,
}) {
  return (
    isDocFlowActiveWidgetSplitting(activeWidget) &&
    !isWidgetInProgress(processData?.[DocFlowMappingProcessCell.SplittingPhase])
  )
}

export function isSelectBoxAreaDisabled({ activeWidget, processData }) {
  return (
    isDocFlowWidgetMappingDisabled({ activeWidget, processData }) ||
    isDocFlowWidgetSplittingDisabled({ activeWidget, processData })
  )
}

export function isDocFlowMappingResetButtonDisabled(processData) {
  return (
    !isDocFlowWidgetStatusCompleted(
      processData?.[DocFlowMappingProcessCell.SelectTypePhase]
    ) || !processData?.[DocFlowMappingProcessCell.AwsProcessFinishedAt]
  )
}

export function loadDocFlowMappingResultSourcePdf(activeFileBytes) {
  return PDFDocument.load(new Uint8Array(activeFileBytes))
}

export async function createDocFlowMappingResultPdfUrl({
  pagesRange,
  sourcePdf,
}) {
  const [startPage, endPage] = pagesRange

  const newPdfDoc = await PDFDocument.create()

  const copiedPages = await newPdfDoc.copyPages(
    sourcePdf,
    range(startPage - 1, endPage)
  )

  copiedPages.forEach(page => newPdfDoc.addPage(page))

  const newPdfBytes = await newPdfDoc.save()
  const blob = new Blob([newPdfBytes], { type: 'application/pdf' })

  return URL.createObjectURL(blob)
}

export async function generateDocFlowMappingResultPdfUrl({
  activeFileBytes,
  pagesRange,
}) {
  if (!activeFileBytes || !pagesRange) {
    return null
  }

  const sourcePdf = await loadDocFlowMappingResultSourcePdf(activeFileBytes)

  return createDocFlowMappingResultPdfUrl({ pagesRange, sourcePdf })
}

export function revokeDocFlowMappingResultPdfUrl(url) {
  if (url) {
    URL.revokeObjectURL(url)
  }
}

export function preventOverflow(pdfPageCoordinates) {
  return {
    async fn({ middlewareData, rects, x, y }) {
      const returnedValue = {
        x,
        y,
      }

      if (!pdfPageCoordinates) {
        return returnedValue
      }

      const { floating, reference } = rects
      const { offset } = middlewareData

      if (x < pdfPageCoordinates.x) {
        returnedValue.x = reference.x
      }

      if (y < pdfPageCoordinates.y) {
        const offsetY = offset.y < 0 ? offset.y * -1 : offset.y

        returnedValue.y = reference.y + reference.height + offsetY
      }

      if (x + floating.width > pdfPageCoordinates.right) {
        returnedValue.x = reference.x + reference.width - floating.width
      }

      return returnedValue
    },
    name: 'preventOverflow',
  }
}

export function isDocFlowMappingIsFileAwsProcess(processData, isActive) {
  return (
    isDocFlowWidgetStatusCompleted(
      processData?.[DocFlowMappingProcessCell.SelectTypePhase]
    ) &&
    !processData?.[DocFlowMappingProcessCell.AwsProcessFinishedAt] &&
    isActive
  )
}

export function getDocFlowMappingNoRecipientNotification(t) {
  return {
    text: t('DocumentStapling.NoRecipientWillBeDeletedNotificationTextKey'),
    type: NOTIFICATION_TYPE.warning,
  }
}

export function getDocFlowMappingWidgetArea(processData) {
  return processData?.[DocFlowMappingProcessCell.HasMappingArea]
    ? processData?.[DocFlowMappingProcessCell.MappingArea]
    : EMPTY_LIST
}

export function getDocFlowMappingWidgetSplittingArea(processData) {
  return processData?.[DocFlowMappingProcessCell.HasSplittingArea]
    ? processData?.[DocFlowMappingProcessCell.SplittingArea]
    : EMPTY_LIST
}
